import React from 'react';

interface PieChartData {
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieChartData[];
  title?: string;
  size?: number;
  className?: string;
}

const PieChart: React.FC<PieChartProps> = ({
  data,
  title,
  size = 300,
  className = '',
}) => {
  // Filter out zero values for pie chart calculation
  const nonZeroData = data.filter((item) => item.value > 0);

  // Calculate total value from non-zero items
  const total = nonZeroData.reduce((acc, item) => acc + item.value, 0);

  // Calculate percentages and angles for non-zero items
  let startAngle = 0;
  const segments = nonZeroData.map((item) => {
    const percentage = (item.value / total) * 100;
    const angle = (percentage / 100) * 360;
    const segment = {
      ...item,
      percentage,
      startAngle,
      endAngle: startAngle + angle,
    };
    startAngle += angle;
    return segment;
  });

  // Create segments for zero values (for legend only)
  const allSegments = data.map((item) => {
    if (item.value === 0) {
      return {
        ...item,
        percentage: 0,
        startAngle: 0,
        endAngle: 0,
      };
    }
    return (
      segments.find((seg) => seg.label === item.label) || {
        ...item,
        percentage: 0,
        startAngle: 0,
        endAngle: 0,
      }
    );
  });

  const radius = size / 2;
  const center = size / 2;

  // Function to create SVG arc path
  const createArc = (startAngle: number, endAngle: number, radius: number) => {
    // Convert angles to radians
    const startRad = (startAngle - 90) * (Math.PI / 180);
    const endRad = (endAngle - 90) * (Math.PI / 180);

    // Calculate coordinates
    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);

    // Determine large-arc-flag (1 if angle > 180 degrees)
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    // Create SVG path
    return `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };

  // Special case: If only one non-zero value exists, draw a full circle
  const renderSingleValueCircle = () => {
    if (segments.length === 1) {
      return (
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill={segments[0].color}
          stroke="#fff"
          strokeWidth="1"
        />
      );
    }
    return null;
  };

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {title && <h3 className="text-xl font-semibold mb-3">{title}</h3>}

      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {segments.length === 1
            ? renderSingleValueCircle()
            : segments.map((segment, index) => (
                <path
                  key={index}
                  d={createArc(segment.startAngle, segment.endAngle, radius)}
                  fill={segment.color}
                  stroke="#fff"
                  strokeWidth="1"
                />
              ))}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center mt-4 gap-4">
        {allSegments.map((segment, index) => (
          <div key={index} className="flex items-center">
            <div
              className="w-4 h-4 mr-2"
              style={{ backgroundColor: segment.color }}
            />
            <span className="text-sm">
              {segment.label} ({segment.value}){' '}
              {segment.percentage > 0
                ? `${segment.percentage.toFixed(0)}%`
                : '0%'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PieChart;
